import { NgModule, ErrorHandler, LOCALE_ID } from "@angular/core";
import { HttpClientModule } from "@angular/common/http";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { IonicApp, IonicModule } from "ionic-angular";
import { MyApp } from "./app.component";
require("intersection-observer");

import { AppConfig } from "./app.config";

// plugins
import { AngularFireModule } from "angularfire2";
import {
  AngularFireDatabaseModule,
  AngularFireDatabase,
} from "angularfire2/database";
import {
  AngularFireStorageModule,
  AngularFireStorage,
} from "angularfire2/storage";
// cordova
import { StatusBar } from "@ionic-native/status-bar";
import { SplashScreen } from "@ionic-native/splash-screen";
import { Device } from "@ionic-native/device";
import { Facebook } from "@ionic-native/facebook";
import { Geolocation } from "@ionic-native/geolocation";
import { IonicStorageModule } from "@ionic/storage";
import { PhotoViewer } from "@ionic-native/photo-viewer";
import { SMS } from "@ionic-native/sms";
import { ImagePicker } from "@ionic-native/image-picker";
import { Camera } from "@ionic-native/camera";
import { Vibration } from "@ionic-native/vibration";
import { QRScanner } from "@ionic-native/qr-scanner";
import { DatePicker } from "@ionic-native/date-picker";
import { Clipboard } from "@ionic-native/clipboard";
import { SocialSharing } from "@ionic-native/social-sharing";
import { Deeplinks } from "@ionic-native/deeplinks";
import { Firebase } from "@ionic-native/firebase";
import { SafariViewController } from "@ionic-native/safari-view-controller";
import { Push } from "@ionic-native/push";
import { Keyboard } from "@ionic-native/keyboard";
import { InAppPurchase } from "@ionic-native/in-app-purchase";
import { IonicImageViewerModule } from "ionic-img-viewer";

// providers
import { Api } from "../providers/api/api";
import { Auth } from "../providers/auth/auth";
import { UserProvider } from "../providers/user/user";
import { TemplateProvider } from "../providers/template/template";
import { ChatRoomProvider } from "../providers/chat-room/chat-room";
import { Tracker } from "../providers/tracker/tracker";
import { GeoProvider } from "../providers/geo/geo";
import { NoticeSettingProvider } from "../providers/notice-setting/notice-setting";
import { Util } from "../providers/util/util";
import { FirebasePluginProvider } from "../providers/firebase/firebase-plugin";
import { AngularFireProvider } from "../providers/firebase/angularfire";
import { DeviceProvider } from "../providers/device/device";
import { TalkProvider } from "../providers/talk/talk";
import { DatetimeTimerProvider } from "../providers/datetime-timer/datetime-timer";
import { MaintenanceProvider } from "../providers/maintenance/maintenance";
import { MasterDataProvider } from "../providers/master-data/master-data";
import { ToastProvider } from "../providers/toast/toast";
import { CameraProvider } from "../providers/camera/camera";

import { registerLocaleData } from "@angular/common";
import localeJa from "@angular/common/locales/ja";
import { BlockProvider } from "../providers/block/block";
import { ReportProvider } from "../providers/report/report";
import { SearchParamsProvider } from "../providers/search-params/search-params";
import { ChatSearchParamsProvider } from "../providers/chat-search-params/chat-search-params";
import { SearchParamsMeetingProvider } from "../providers/search-params-meeting/search-params-meeting";
import { SearchParamsBoardsProvider } from "../providers/search-params-boards/search-params-boards";
import { AdminManagedUserProvider } from "../providers/admin-managed-user/admin-managed-user";
import { WebViewProvider } from "../providers/web-view/web-view";
import { NoticesProvider } from "../providers/notices/notices";
import { UnreadNumProvider } from "../providers/unread-num/unread-num";
import { AgeVerificationProvider } from "../providers/confirmation-image/confirmation-image";
import { ContactProvider } from "../providers/contact/contact";
import { PurchaseProvider } from "../providers/purchase/purchase";
import { IapProvider } from "../providers/iap/iap";
import { MeetingEntryProvider } from "../providers/meeting-entry/meeting-entry";
import { ClientLogProvider } from "../providers/client-log/client-log";
import { GlobalErrorHandler } from "./error-handler";
import { FootprintProvider } from "../providers/footprint/footprint";
import { WebToAppPopoverProvider } from "../providers/popover/web-to-app-popover/web-to-app-popover";
import { PopoverProvider } from "../providers/popover/popover";
import { DeeplinksProvider } from "../providers/deeplinks/deeplinks";
import { BirthdayChangeProvider } from "../providers/birthday-change/birthday-change";
import { LocalstorageProvider } from "../providers/localstorage/localstorage";
import { BackdropProvider } from "../providers/backdrop/backdrop";
import { IncomeProofProvider } from "../providers/confirmation-image/confirmation-image";
import { AssetProofProvider } from "../providers/confirmation-image/confirmation-image";
import { Api4Asp } from "../providers/api/api4asp";
import { CalendarProvider } from "../providers/calendar/calendar";
import { PointProvider } from "../providers/point/point";
import { ChanceTimeProvider } from "../providers/chance-time/chance-time";
import { QualityCheckProvider } from "../providers/quality-check/quality-check";
import { DatedSurveyProvider } from "../providers/dated-survey/dated-survey";
import { PhoneAuthProvider } from "../providers/phone-auth/phone-auth";
import { VideoProvider } from "../providers/video/video";
import { AppointmentProvider } from "../providers/appointment/appointment";
import { BoardProvider } from "../providers/board/board";

registerLocaleData(localeJa);

@NgModule({
  declarations: [MyApp],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    IonicStorageModule.forRoot(),
    AngularFireModule.initializeApp(AppConfig.ANGULAR_FIRE_CONFIG),
    AngularFireDatabaseModule,
    AngularFireStorageModule,
    IonicModule.forRoot(MyApp, {
      backButtonText: "　",
    }),
    IonicImageViewerModule,
  ],
  bootstrap: [IonicApp],
  entryComponents: [MyApp],
  providers: [
    StatusBar,
    SplashScreen,
    Device,
    Facebook,
    Geolocation,
    PhotoViewer,
    SMS,
    ImagePicker,
    Camera,
    Vibration,
    QRScanner,
    DatePicker,
    Clipboard,
    SocialSharing,
    Deeplinks,
    Firebase,
    SafariViewController,
    Push,
    Keyboard,
    InAppPurchase,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: LOCALE_ID, useValue: "ja" },
    Api,
    UserProvider,
    TemplateProvider,
    ChatRoomProvider,
    Auth,
    Tracker,
    GeoProvider,
    NoticeSettingProvider,
    Util,
    AngularFireDatabase,
    AngularFireStorage,
    AngularFireProvider,
    FirebasePluginProvider,
    DeviceProvider,
    TalkProvider,
    DatetimeTimerProvider,
    MaintenanceProvider,
    MasterDataProvider,
    ToastProvider,
    CameraProvider,
    BlockProvider,
    ReportProvider,
    SearchParamsProvider,
    ChatSearchParamsProvider,
    SearchParamsMeetingProvider,
    SearchParamsBoardsProvider,
    AdminManagedUserProvider,
    WebViewProvider,
    NoticesProvider,
    UnreadNumProvider,
    AgeVerificationProvider,
    ContactProvider,
    PurchaseProvider,
    IapProvider,
    MeetingEntryProvider,
    ClientLogProvider,
    FootprintProvider,
    WebToAppPopoverProvider,
    PopoverProvider,
    DeeplinksProvider,
    BirthdayChangeProvider,
    BackdropProvider,
    LocalstorageProvider,
    IncomeProofProvider,
    AssetProofProvider,
    Api4Asp,
    CalendarProvider,
    PointProvider,
    ChanceTimeProvider,
    DatedSurveyProvider,
    PhoneAuthProvider,
    QualityCheckProvider,
    PhoneAuthProvider,
    VideoProvider,
    AppointmentProvider,
    BoardProvider,
  ],
})
export class AppModule {}


// WEBPACK FOOTER //
// ./src/app/app.module.ts